import { useQuery } from '@tanstack/react-query';
import { useCompany } from '../hooks/useCompany';
import { consolidationAPI } from '../lib/api';
import { Building2, Users, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const CHART_COLORS = ['#0066B3', '#00A651', '#006837', '#009B77', '#0A2240', '#4A90D9', '#2DD881'];

const formatCurrency = (value, currency = 'USD') => {
  if (currency === 'LRD') {
    return `L$${new Intl.NumberFormat('en-US').format(value || 0)}`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

export default function Consolidation() {
  const { selectedCompany, isParent } = useCompany();

  const { data: consolidation, isLoading } = useQuery({
    queryKey: ['consolidation', selectedCompany?.id],
    queryFn: () => consolidationAPI.getData(selectedCompany?.id).then((res) => res.data),
    enabled: !!selectedCompany?.id && isParent,
  });

  if (!isParent) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]" data-testid="consolidation-page">
        <Building2 className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Consolidation View</h2>
        <p className="text-muted-foreground text-center max-w-md">
          Please select the parent company (DEA Group, Inc.) to view consolidated financial data across all subsidiaries.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="consolidation-page">
        <div className="animate-pulse">
          <div className="h-8 w-64 bg-muted rounded mb-2"></div>
          <div className="h-4 w-96 bg-muted rounded"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card-surface p-6 animate-pulse">
              <div className="h-4 w-24 bg-muted rounded mb-2"></div>
              <div className="h-8 w-32 bg-muted rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const subsidiaryData = consolidation?.subsidiaries || [];
  // Charts show the USD figure only -- a bar/pie can't meaningfully mix two
  // currencies in one value; the table below shows both currencies in full.
  const revenueData = subsidiaryData.map((sub) => ({
    name: sub.name.replace('DEA ', '').replace('DFS Financial Services', 'DFS'),
    revenue: sub.revenue_usd || 0,
    profit: sub.profit_usd || 0,
  }));

  const pieData = subsidiaryData.map((sub) => ({
    name: sub.name.replace('DEA ', '').replace('DFS Financial Services', 'DFS'),
    value: sub.revenue_usd || 0,
  }));

  return (
    <div className="space-y-8" data-testid="consolidation-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Consolidated Overview</h1>
        <p className="text-muted-foreground mt-1">
          Financial summary across all DEA Group subsidiaries
        </p>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="card-surface p-6">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Subsidiaries</span>
          </div>
          <p className="text-3xl font-semibold">{consolidation?.subsidiaryCount || 0}</p>
        </div>

        <div className="card-surface p-6">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Total Revenue</span>
          </div>
          <p className="text-3xl font-semibold text-green-600">
            {formatCurrency(consolidation?.totalRevenueUsd)}
          </p>
          {consolidation?.totalRevenueLrd > 0 && (
            <p className="text-sm font-medium text-blue-600">{formatCurrency(consolidation.totalRevenueLrd, 'LRD')}</p>
          )}
        </div>

        <div className="card-surface p-6">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Net Income</span>
          </div>
          <p className={`text-3xl font-semibold ${consolidation?.netIncomeUsd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(consolidation?.netIncomeUsd)}
          </p>
          {!!consolidation?.netIncomeLrd && (
            <p className={`text-sm font-medium ${consolidation.netIncomeLrd >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
              {formatCurrency(consolidation.netIncomeLrd, 'LRD')}
            </p>
          )}
        </div>

        <div className="card-surface p-6">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Total Employees</span>
          </div>
          <p className="text-3xl font-semibold">{consolidation?.totalEmployees || 0}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid gap-8 lg:grid-cols-2">
        {/* Revenue by Subsidiary */}
        <div className="card-surface p-6">
          <h3 className="text-base font-semibold mb-4">Revenue by Subsidiary</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  type="number"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  width={80}
                />
                <Tooltip formatter={(value) => [formatCurrency(value), '']} />
                <Bar dataKey="revenue" fill="#0066B3" radius={[0, 4, 4, 0]} name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Distribution */}
        <div className="card-surface p-6">
          <h3 className="text-base font-semibold mb-4">Revenue Distribution</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {pieData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatCurrency(value), '']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Subsidiary Details Table */}
      <div className="card-surface overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="text-base font-semibold">Subsidiary Performance</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="table-header">
              <th className="text-left p-4">Subsidiary</th>
              <th className="text-right p-4">Revenue</th>
              <th className="text-right p-4">Expenses</th>
              <th className="text-right p-4">Profit/Loss</th>
              <th className="text-right p-4">Employees</th>
            </tr>
          </thead>
          <tbody>
            {subsidiaryData.map((sub) => (
              <tr key={sub.id} className="border-t border-border hover:bg-muted/50 transition-colors">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{sub.name}</p>
                      <p className="text-xs text-muted-foreground">{sub.code}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-right font-mono">
                  {formatCurrency(sub.revenue_usd)}
                  {sub.revenue_lrd > 0 && <div className="text-xs text-blue-600">{formatCurrency(sub.revenue_lrd, 'LRD')}</div>}
                </td>
                <td className="p-4 text-right font-mono">
                  {formatCurrency(sub.expenses_usd)}
                  {sub.expenses_lrd > 0 && <div className="text-xs text-blue-600">{formatCurrency(sub.expenses_lrd, 'LRD')}</div>}
                </td>
                <td className="p-4 text-right">
                  <span className={`font-mono font-semibold ${sub.profit_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(sub.profit_usd)}
                  </span>
                  {!!sub.profit_lrd && (
                    <div className={`font-mono text-xs ${sub.profit_lrd >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                      {formatCurrency(sub.profit_lrd, 'LRD')}
                    </div>
                  )}
                </td>
                <td className="p-4 text-right">{sub.employees}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-muted/50 font-semibold">
              <td className="p-4">Total</td>
              <td className="p-4 text-right font-mono">
                {formatCurrency(consolidation?.totalRevenueUsd)}
                {consolidation?.totalRevenueLrd > 0 && <div className="text-xs text-blue-600">{formatCurrency(consolidation.totalRevenueLrd, 'LRD')}</div>}
              </td>
              <td className="p-4 text-right font-mono">
                {formatCurrency(consolidation?.totalExpensesUsd)}
                {consolidation?.totalExpensesLrd > 0 && <div className="text-xs text-blue-600">{formatCurrency(consolidation.totalExpensesLrd, 'LRD')}</div>}
              </td>
              <td className={`p-4 text-right font-mono ${consolidation?.netIncomeUsd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(consolidation?.netIncomeUsd)}
                {!!consolidation?.netIncomeLrd && (
                  <div className={`text-xs ${consolidation.netIncomeLrd >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                    {formatCurrency(consolidation.netIncomeLrd, 'LRD')}
                  </div>
                )}
              </td>
              <td className="p-4 text-right">{consolidation?.totalEmployees}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
